/**
 * networkErrors.ts
 *
 * Recognises requests that failed in the network (offline, flaky mobile
 * connection, connection reset) rather than in our code or on the server, so
 * they are kept as Sentry breadcrumbs instead of being reported as errors.
 */
import { ConnectError } from "@connectrpc/connect";
import * as Sentry from "@sentry/vue";
import type { Event, EventHint } from "@sentry/vue";

/**
 * Messages browsers give a `fetch` that never got a response.
 *
 * Matched against the start of the message, because Sentry appends the host
 * (e.g. "Failed to fetch (tinhtien24.com)") when it enriches fetch errors.
 */
const NETWORK_FAILURE_MESSAGES: readonly RegExp[] = [
  /^Failed to fetch\b/i, // Chrome, Edge
  /^Load failed\b/i, // Safari
  /^NetworkError when attempting to fetch resource/i, // Firefox
  /^Network request failed\b/i,
  /^The network connection was lost/i, // Safari / iOS WebView
  /^The Internet connection appears to be offline/i, // Safari / iOS WebView
];

/** How deep to follow `cause` links; the real chain is at most two long. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Returns the network-failure message an error carries, if any.
 *
 * Only a `TypeError` (how `fetch` reports it) or a `ConnectError` (how the
 * Connect transport wraps it, as `Code.Unknown` with the TypeError as `cause`)
 * counts, so a code bug that happens to say "Load failed" is still reported.
 *
 * @param error - Anything thrown.
 * @returns The browser's message, or undefined when this is not a network failure.
 */
export function networkFailureMessage(error: unknown): string | undefined {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current instanceof Error; depth++) {
    const message = current instanceof ConnectError ? current.rawMessage : current.message;
    const isFetchShaped = current instanceof TypeError || current instanceof ConnectError;
    if (isFetchShaped && NETWORK_FAILURE_MESSAGES.some((pattern) => pattern.test(message))) {
      return message;
    }
    current = current.cause;
  }
  return undefined;
}

/**
 * Reports whether an error is a network failure.
 *
 * @param error - Anything thrown.
 * @returns True when {@link networkFailureMessage} recognises it.
 */
export function isNetworkFailure(error: unknown): boolean {
  return networkFailureMessage(error) !== undefined;
}

/**
 * Sentry event processor that turns network failures into breadcrumbs.
 *
 * The event is dropped, and a `network` breadcrumb is left in its place, so a
 * later real error still shows that the connection failed just before it.
 *
 * @param event - The event about to be sent.
 * @param hint - Carries the original thrown value.
 * @returns The event, or null when it was a network failure.
 */
export function networkFailuresAsBreadcrumbs(event: Event, hint: EventHint): Event | null {
  const message = networkFailureMessage(hint.originalException);
  if (message === undefined) return event;

  Sentry.addBreadcrumb({
    category: "network",
    level: "warning",
    message: `Request failed: ${message}`,
    data: { online: typeof navigator === "undefined" ? undefined : navigator.onLine },
  });
  return null;
}
